import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';

export interface ChecklistCategory {
  id: string;
  center_id: string;
  name: string;
  order_index: number;
  color?: string;
}

export interface ChecklistQuestion {
  id: string;
  center_id: string;
  category_id?: string;
  text: string;
  order_index: number;
}

interface ChecklistStore {
  loading: boolean;
  error: string | null;
  categories: ChecklistCategory[];
  questions: ChecklistQuestion[];
  useChecklistCategories: boolean;
  centerDescription: string;
  centerType: string;

  fetchStructure: (centerId: string) => Promise<void>;
  updateCenterSettings: (centerId: string, enabled: boolean) => Promise<void>;
  updateCenterDetails: (centerId: string, description: string, type?: string) => Promise<void>;

  createCategory: (centerId: string, name: string) => Promise<ChecklistCategory | void>;
  updateCategory: (id: string, updates: Partial<ChecklistCategory>) => Promise<void>;
  deleteCategory: (id: string, centerId: string) => Promise<void>;
  reorderCategories: (categories: ChecklistCategory[]) => Promise<void>;

  createQuestion: (centerId: string, text: string, categoryId?: string) => Promise<void>;
  updateQuestion: (id: string, updates: Partial<ChecklistQuestion>) => Promise<void>;
  deleteQuestion: (id: string, centerId: string) => Promise<void>;
  deleteAllChecklistData: (centerId: string) => Promise<void>;
  reorderQuestions: (questions: ChecklistQuestion[]) => Promise<void>;
}

const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const useChecklistStore = create<ChecklistStore>()(persist<ChecklistStore>((set, get) => ({
  loading: false,
  error: null,
  categories: [],
  questions: [],
  useChecklistCategories: true, // Always true
  centerDescription: '',
  centerType: 'sell',

  fetchStructure: async (centerId: string) => {
    console.log('[ChecklistStore] Fetching structure for center:', centerId);
    set({ loading: true, error: null });
    try {
      const response = await api.get(`/checklist/structure/${centerId}`);
      console.log('[ChecklistStore] Structure loaded:', response.data);
      const data = response.data;

      // Sanitize default description
      const rawDescription = data.description || '';
      const sanitizedDescription = rawDescription === 'Yaron for Family Economics' ? '' : rawDescription;

      set({
        categories: data.categories || [],
        questions: data.questions || [],
        useChecklistCategories: true, // Force true from backend as well
        centerDescription: sanitizedDescription,
        centerType: data.type || 'sell',
        loading: false
      });
    } catch (err: any) {
      console.error('[ChecklistStore] Error fetching structure:', err);
      set({ error: err.message || 'Failed to fetch checklist structure', loading: false });
    }
  },

  updateCenterSettings: async (_centerId: string, _enabled: boolean) => {
    // Deprecated: No-op or log warning. We don't want to allow disabling categories.
    console.warn('[ChecklistStore] updateCenterSettings is deprecated. Categories are now mandatory.');
    // Optionally ensure it stays true locally
    set({ useChecklistCategories: true });
  },

  updateCenterDetails: async (centerId: string, description: string, type?: string) => {
    console.log('[ChecklistStore] Updating center details:', { centerId, description, type });
    try {
      const updates: any = { description };
      if (type !== undefined) updates.type = type;

      await api.patch(`/centers/${centerId}`, updates);

      const newState: any = { centerDescription: description };
      if (type !== undefined) newState.centerType = type;
      set(newState);

      console.log('[ChecklistStore] Center details updated successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error updating center details:', err);
      set({ error: err.message || 'Failed to update center details' });
      throw err;
    }
  },

  createCategory: async (centerId: string, name: string) => {
    console.log('[ChecklistStore] Creating category:', { centerId, name });
    const currentCategories = get().categories;
    const nextOrderIndex = currentCategories.length > 0
      ? Math.max(...currentCategories.map(c => c.order_index)) + 1
      : 0;

    const randomColor = getRandomColor();

    // Optimistic Update
    const tempId = `temp-${Date.now()}`;
    const tempCategory: ChecklistCategory = {
      id: tempId,
      center_id: centerId,
      name,
      order_index: nextOrderIndex,
      color: randomColor
    };

    set(state => ({ categories: [...state.categories, tempCategory] }));

    try {
      const response = await api.post('/checklist/category', { center_id: centerId, name, order_index: nextOrderIndex, color: randomColor });
      console.log('[ChecklistStore] Category created:', response.data);
      // Replace temp category with real one
      set(state => ({
        categories: state.categories.map(c => c.id === tempId ? response.data : c)
      }));
      return response.data;
    } catch (err: any) {
      console.error('[ChecklistStore] Error creating category:', err);
      // Revert optimistic update
      set(state => ({
        error: err.message || 'Failed to create category',
        categories: state.categories.filter(c => c.id !== tempId)
      }));
    }
  },

  updateCategory: async (id: string, updates: Partial<ChecklistCategory>) => {
    console.log('[ChecklistStore] Updating category:', { id, updates });
    try {
      // Optimistic update
      set(state => ({
        categories: state.categories.map(c => c.id === id ? { ...c, ...updates } : c)
      }));
      await api.put(`/checklist/category/${id}`, updates);
      console.log('[ChecklistStore] Category updated successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error updating category:', err);
      set({ error: err.message || 'Failed to update category' });
    }
  },

  deleteCategory: async (id: string, centerId: string) => {
    console.log('[ChecklistStore] Deleting category and its questions:', id);
    const previousState = {
      categories: get().categories,
      questions: get().questions,
    };
    const categoryQuestions = previousState.questions.filter(q => q.category_id === id);

    try {
      if (id.startsWith('temp-')) {
        set(state => ({
          categories: state.categories.filter(c => c.id !== id),
          questions: state.questions.filter(q => q.category_id !== id),
          error: null
        }));
        return;
      }

      for (const question of categoryQuestions) {
        if (question.id.startsWith('temp-')) continue;
        await api.delete(`/checklist/question/${question.id}`);
      }

      await api.delete(`/checklist/category/${id}`);

      set(state => ({
        categories: state.categories.filter(c => c.id !== id),
        questions: state.questions.filter(q => q.category_id !== id),
        error: null
      }));

      console.log('[ChecklistStore] Category deleted successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error deleting category:', err);
      set({
        categories: previousState.categories,
        questions: previousState.questions,
        error: err.message || 'Failed to delete category'
      });

      try {
        await get().fetchStructure(centerId);
      } catch (refreshErr) {
        console.error('[ChecklistStore] Failed to refresh structure after deleteCategory error:', refreshErr);
      }
    }
  },

  reorderCategories: async (reorderedCategories: ChecklistCategory[]) => {
    console.log('[ChecklistStore] Reordering categories');
    set({ categories: reorderedCategories });

    try {
      await Promise.all(reorderedCategories.map((cat, index) =>
        api.put(`/checklist/category/${cat.id}`, { order_index: index })
      ));
      console.log('[ChecklistStore] Categories reordered successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error reordering categories:', err);
      set({ error: "Failed to save new order" });
    }
  },

  createQuestion: async (centerId: string, text: string, categoryId?: string) => {
    console.log('[ChecklistStore] Creating question:', { centerId, text, categoryId });
    const currentQuestions = get().questions.filter(q => q.category_id === categoryId);
    const nextOrderIndex = currentQuestions.length > 0
      ? Math.max(...currentQuestions.map(q => q.order_index)) + 1
      : 0;

    // Optimistic Update
    const tempId = `temp-${Date.now()}`;
    const tempQuestion: ChecklistQuestion = {
      id: tempId,
      center_id: centerId,
      category_id: categoryId,
      text,
      order_index: nextOrderIndex
    };

    set(state => ({ questions: [...state.questions, tempQuestion] }));

    try {
      const response = await api.post('/checklist/question', {
        center_id: centerId,
        text,
        category_id: categoryId,
        order_index: nextOrderIndex
      });
      console.log('[ChecklistStore] Question created:', response.data);
      // Replace with real data
      set(state => ({
        questions: state.questions.map(q => q.id === tempId ? response.data : q)
      }));
    } catch (err: any) {
      console.error('[ChecklistStore] Error creating question:', err);
      // Revert
      set(state => ({
        error: err.message || 'Failed to create question',
        questions: state.questions.filter(q => q.id !== tempId)
      }));
    }
  },

  updateQuestion: async (id: string, updates: Partial<ChecklistQuestion>) => {
    console.log('[ChecklistStore] Updating question:', { id, updates });
    try {
      set(state => ({
        questions: state.questions.map(q => q.id === id ? { ...q, ...updates } : q)
      }));
      await api.put(`/checklist/question/${id}`, updates);
      console.log('[ChecklistStore] Question updated successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error updating question:', err);
      set({ error: err.message || 'Failed to update question' });
    }
  },

  deleteQuestion: async (id: string, _centerId: string) => {
    console.log('[ChecklistStore] Deleting question:', id);
    try {
      await api.delete(`/checklist/question/${id}`);
      set(state => ({
        questions: state.questions.filter(q => q.id !== id)
      }));
      console.log('[ChecklistStore] Question deleted successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error deleting question:', err);
      set({ error: err.message || 'Failed to delete question' });
    }
  },

  deleteAllChecklistData: async (centerId: string) => {
    console.log('[ChecklistStore] Deleting all checklist data for center:', centerId);
    const previousState = {
      categories: get().categories,
      questions: get().questions,
    };

    const centerQuestions = get().questions.filter(
      (q) => q.center_id === centerId && !q.id.startsWith('temp-')
    );
    const centerCategories = get().categories.filter(
      (c) => c.center_id === centerId && !c.id.startsWith('temp-')
    );

    try {
      for (const question of centerQuestions) {
        await api.delete(`/checklist/question/${question.id}`);
      }

      for (const category of centerCategories) {
        await api.delete(`/checklist/category/${category.id}`);
      }

      set(state => ({
        questions: state.questions.filter(q => q.center_id !== centerId),
        categories: state.categories.filter(c => c.center_id !== centerId),
        error: null
      }));

      console.log('[ChecklistStore] All checklist data deleted successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error deleting all checklist data:', err);
      set({
        categories: previousState.categories,
        questions: previousState.questions,
        error: err.message || 'Failed to delete all checklist data'
      });
      try {
        await get().fetchStructure(centerId);
      } catch (refreshErr) {
        console.error('[ChecklistStore] Failed to refresh structure after deleteAllChecklistData error:', refreshErr);
      }
      throw err;
    }
  },

  reorderQuestions: async (reorderedQuestions: ChecklistQuestion[]) => {
    console.log('[ChecklistStore] Reordering questions subset:', reorderedQuestions.length);

    // 1. Assign new order indices to the subset based on their array position
    const updatedSubset = reorderedQuestions.map((q, index) => ({
      ...q,
      order_index: index
    }));

    // 2. Optimistically update store: Merge updated subset into existing questions
    console.log('[ChecklistStore] Optimistic Update - New Order Indices:', updatedSubset.map(q => `${q.id}:${q.order_index}`));
    set(state => ({
      questions: state.questions.map(q => {
        const updatedVideo = updatedSubset.find(uq => uq.id === q.id);
        return updatedVideo ? updatedVideo : q;
      })
    }));

    try {
      // 3. Send updates only for the subset
      await Promise.all(updatedSubset.map((q) =>
        api.put(`/checklist/question/${q.id}`, { order_index: q.order_index })
      ));
      console.log('[ChecklistStore] Questions reordered successfully');
    } catch (err: any) {
      console.error('[ChecklistStore] Error reordering questions:', err);
      // Ideally revert here, but for now just show error
      set({ error: "Failed to persist question order" });
    }
  }
}), {
  name: 'checklist-store',
}));
